import React from 'react';
import { CompositePricingTextField } from './CompositePricingFields';
import { TierCommonField } from '../../utils/tierCompositePricing';

interface CompositeFeeGroupSettingsProps {
  values: {
    extraFee1Percent?: number;
    extraFee1VatPercent?: number;
    extraFee1Fixed?: number;
    extraFee1Name?: string | null;
    extraFee2Percent?: number;
    extraFee2VatPercent?: number;
    extraFee2Fixed?: number;
    extraFee2Name?: string | null;
  };
  onNumberChange: (field: TierCommonField, value: number | null) => void;
  onTextChange: (field: CompositePricingTextField, value: string) => void;
  formatNumberDisplay: (value: number | string) => string;
  parseNumberInput: (value: string) => string;
  t: (key: string) => string;
}

const CompositeFeeGroupSettings: React.FC<CompositeFeeGroupSettingsProps> = ({
  values,
  onNumberChange,
  onTextChange,
  formatNumberDisplay,
  parseNumberInput,
  t,
}) => {
  const percentInput = (field: TierCommonField, value: number | undefined) => (
    <input
      type="number"
      step="0.01"
      min="0"
      max="100"
      value={value ?? 0}
      onChange={(e) => onNumberChange(field, parseFloat(e.target.value) || 0)}
      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
    />
  );

  const fixedInput = (field: TierCommonField, value: number | undefined) => (
    <input
      type="text"
      value={formatNumberDisplay(value ?? 0)}
      onChange={(e) => {
        const raw = parseNumberInput(e.target.value);
        onNumberChange(field, raw === '' ? null : parseFloat(raw) || 0);
      }}
      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
    />
  );

  const nameInput = (field: CompositePricingTextField, value: string | null | undefined) => (
    <input
      type="text"
      value={value ?? ''}
      onChange={(e) => onTextChange(field, e.target.value)}
      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
      placeholder={t('extraFeeGroupNamePlaceholder')}
    />
  );

  const renderGroup = (
    title: string,
    nameField: CompositePricingTextField,
    percentField: TierCommonField,
    vatPercentField: TierCommonField,
    fixedField: TierCommonField,
    nameValue: string | null | undefined,
    percentValue: number | undefined,
    vatPercentValue: number | undefined,
    fixedValue: number | undefined,
  ) => (
    <div className="border border-amber-200 rounded-lg p-4 bg-amber-50/60">
      <h5 className="text-sm font-semibold text-gray-800 mb-3">{title}</h5>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="md:col-span-2 lg:col-span-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">{t('extraFeeGroupNameLabel')}</label>
          {nameInput(nameField, nameValue)}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">{t('extraFeePercentLabel')}</label>
          {percentInput(percentField, percentValue)}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">{t('extraFeeVatPercentLabel')}</label>
          {percentInput(vatPercentField, vatPercentValue)}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">{t('extraFeeFixedLabel')}</label>
          {fixedInput(fixedField, fixedValue)}
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600">{t('tierCommonFeeSettingsHint')}</p>
      {renderGroup(
        t('extraFeeGroup1Title'),
        'extraFee1Name',
        'extraFee1Percent',
        'extraFee1VatPercent',
        'extraFee1Fixed',
        values.extraFee1Name,
        values.extraFee1Percent,
        values.extraFee1VatPercent,
        values.extraFee1Fixed,
      )}
      {renderGroup(
        t('extraFeeGroup2Title'),
        'extraFee2Name',
        'extraFee2Percent',
        'extraFee2VatPercent',
        'extraFee2Fixed',
        values.extraFee2Name,
        values.extraFee2Percent,
        values.extraFee2VatPercent,
        values.extraFee2Fixed,
      )}
    </div>
  );
};

export default CompositeFeeGroupSettings;
